import { useMemo } from 'react';
import { useAuthorization } from '@/contexts/AuthorizationContext';
import { can, isAdmin, normalizeRole, type Action, type Resource, type Role } from '@/lib/rbac/permissions';

/**
 * Hook for RBAC authorization checks
 * Uses canonical permission system
 */
export function useRBAC() {
  const { roles, loading, isAdmin: legacyIsAdmin } = useAuthorization();
  
  const role = useMemo<Role>(() => normalizeRole(roles), [roles]);
  
  const checkPermission = (action: Action, resource: Resource): boolean => {
    return can(role, action, resource);
  };
  
  return {
    role,
    loading,
    isAdmin: isAdmin(role),
    can: checkPermission,
    // Convenience helpers
    canView: (resource: Resource) => checkPermission('view', resource),
    canCreate: (resource: Resource) => checkPermission('create', resource),
    canUpdate: (resource: Resource) => checkPermission('update', resource),
    canDelete: (resource: Resource) => checkPermission('delete', resource),
    canManage: (resource: Resource) => checkPermission('manage', resource),
  };
}
